/**
 * Event reminders: when each one is due, and what it says. The event keeps
 * its reminder as minutes before the start (a VALARM on the server); webmail,
 * the desktop app and the phone apps each raise it themselves, as CalDAV
 * clients do.
 */

import type { CalendarEvent } from 'zitimail-shared-types';

/** The choices, in order; the phone apps offer the same list. */
export const REMINDER_CHOICES: ReadonlyArray<number | undefined> = [
  undefined,
  0,
  5,
  10,
  15,
  30,
  60,
  120,
  1440,
  10080,
];

/** "15 minutes before", "1 day before"; any other count of minutes too. */
export function reminderLabel(minutes: number | undefined): string {
  if (minutes === undefined) {
    return 'None';
  }
  if (minutes === 0) {
    return 'At time of event';
  }
  const unit = (n: number, one: string) => `${n} ${one}${n === 1 ? '' : 's'} before`;
  if (minutes % 10080 === 0) {
    return unit(minutes / 10080, 'week');
  }
  if (minutes % 1440 === 0) {
    return unit(minutes / 1440, 'day');
  }
  if (minutes % 60 === 0) {
    return unit(minutes / 60, 'hour');
  }
  return unit(minutes, 'minute');
}

/** The choices for an event: the list, plus its own value when that is not in it. */
export function reminderChoices(current: number | undefined): ReadonlyArray<number | undefined> {
  return REMINDER_CHOICES.includes(current)
    ? REMINDER_CHOICES
    : [...REMINDER_CHOICES, current].sort((a, b) => (a ?? -1) - (b ?? -1));
}

/** When an event starts, as the person sees it: an all-day event at local midnight. */
export function startsAt(event: CalendarEvent): number {
  if (event.allDay) {
    const [y, m, d] = event.start.slice(0, 10).split('-').map(Number);
    return new Date(y ?? 1970, (m ?? 1) - 1, d ?? 1).getTime();
  }
  return Date.parse(event.start);
}

function endsAt(event: CalendarEvent): number {
  if (event.allDay) {
    const [y, m, d] = event.end.slice(0, 10).split('-').map(Number);
    return new Date(y ?? 1970, (m ?? 1) - 1, d ?? 1).getTime();
  }
  return Date.parse(event.end);
}

export interface Reminder {
  /** One occurrence and its reminder: a changed reminder is a new one. */
  readonly key: string;
  readonly event: CalendarEvent;
  readonly fireAt: number;
  readonly startAt: number;
  /** Until then it stands; after, the event is over. */
  readonly until: number;
}

/** Every reminder among `events`, earliest first. */
export function remindersOf(events: readonly CalendarEvent[]): Reminder[] {
  const list: Reminder[] = [];
  const seen = new Set<string>();
  for (const event of events) {
    if (event.reminderMinutes === undefined) {
      continue;
    }
    const key = `${event.uid}|${event.recurrenceId ?? event.start}|${event.reminderMinutes}`;
    if (seen.has(key)) {
      continue;
    }
    seen.add(key);
    const startAt = startsAt(event);
    list.push({
      key,
      event,
      fireAt: startAt - event.reminderMinutes * 60_000,
      startAt,
      until: Math.max(endsAt(event), startAt + 60_000),
    });
  }
  return list.sort((a, b) => a.fireAt - b.fireAt);
}

/**
 * The reminders standing at `now`: due, the event not over, not dismissed.
 * One that came due while webmail was closed still shows, as in Outlook.
 */
export function standing(
  reminders: readonly Reminder[],
  now: number,
  dismissed: ReadonlySet<string>,
): Reminder[] {
  return reminders.filter((r) => r.fireAt <= now && now < r.until && !dismissed.has(r.key));
}

/** When the next reminder comes due after `now`, if one does. */
export function nextDue(reminders: readonly Reminder[], now: number): number | undefined {
  return reminders.find((r) => r.fireAt > now)?.fireAt;
}

/** "Starts in 15 minutes", "Starting now", "Started 5 minutes ago", "Tomorrow". */
export function whenLabel(r: Reminder, now: number): string {
  if (r.event.allDay) {
    const today = new Date(now);
    today.setHours(0, 0, 0, 0);
    const days = Math.round((r.startAt - today.getTime()) / 86_400_000);
    if (days <= 0) {
      return 'Today, all day';
    }
    if (days === 1) {
      return 'Tomorrow, all day';
    }
    return `${new Date(r.startAt).toLocaleDateString([], { weekday: 'long', day: 'numeric', month: 'short' })}, all day`;
  }
  const minutes = Math.round((r.startAt - now) / 60_000);
  if (minutes === 0) {
    return 'Starting now';
  }
  const span = (m: number) => {
    const n = Math.abs(m);
    if (n < 60) {
      return `${n} minute${n === 1 ? '' : 's'}`;
    }
    if (n < 1440) {
      const h = Math.round(n / 60);
      return `${h} hour${h === 1 ? '' : 's'}`;
    }
    const d = Math.round(n / 1440);
    return `${d} day${d === 1 ? '' : 's'}`;
  };
  return minutes > 0 ? `Starts in ${span(minutes)}` : `Started ${span(minutes)} ago`;
}

/** "10:00 – 11:00 · Room 4" (with the day, when it is not today). */
export function timesLabel(r: Reminder, now: number): string {
  const where = r.event.location ? ` · ${r.event.location}` : '';
  if (r.event.allDay) {
    return `All day${where}`;
  }
  const time = (t: number) =>
    new Date(t).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  const sameDay = new Date(r.startAt).toDateString() === new Date(now).toDateString();
  const day = sameDay
    ? ''
    : `${new Date(r.startAt).toLocaleDateString([], { weekday: 'short', day: 'numeric', month: 'short' })}, `;
  return `${day}${time(r.startAt)} – ${time(Date.parse(r.event.end))}${where}`;
}
